
import React, { useState, useEffect, useRef } from "react";
import { Bell } from "lucide-react";
import NotificationItem from "./NotificationItem";
import { fetchNotifications, NotificationItemType } from "./notificationMockData";
import {
  MembershipViewAccessPrompt,
  prepareMembershipViewAccessPrompt,
  requestMembershipViewAccess,
} from "./notificationMembershipAction";
import MembershipViewAccessModal from "./MembershipViewAccessModal";
import { Link } from "react-router-dom";

import { getAuthCookie } from "../../utils/authCookies";

type NotificationDropdownProps = {
  login: string;
};

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ login }) => {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const clickInFlightRef = useRef(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const previousUnreadCountRef = useRef<number>(0);
  const [accessPrompt, setAccessPrompt] = useState<MembershipViewAccessPrompt | null>(null);
  const [requestingAccess, setRequestingAccess] = useState(false);

  const handleNotificationClick = async (item: NotificationItemType) => {
    if (clickInFlightRef.current) return;
    clickInFlightRef.current = true;
    try {
      const { result, prompt } = await prepareMembershipViewAccessPrompt(item);
      if (result === "prompt" && prompt) {
        setOpen(false);
        setAccessPrompt(prompt);
      }
    } finally {
      clickInFlightRef.current = false;
    }
  };

  const handleRequestAccess = async () => {
    if (!accessPrompt || requestingAccess) return;
    setRequestingAccess(true);
    try {
      const sent = await requestMembershipViewAccess(accessPrompt);
      if (sent) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === accessPrompt.notificationId ? { ...n, isUnread: false } : n))
        );
        setUnreadCount((count) => Math.max(0, count - 1));
        setAccessPrompt(null);
      }
    } finally {
      setRequestingAccess(false);
    }
  };

  // ✅ Load notifications on mount
  useEffect(() => {
    const loadNotifications = async () => {
      const globalUserId = String(
        getAuthCookie("globalUserId") || ""
      ).trim();
      const token = getAuthCookie("sessionToken");
      
      if (!globalUserId || !token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const fetchedResult = await fetchNotifications(globalUserId, token, {
          page: 1,
          limit: 10, // Only fetch first 10 for dropdown
          seen: "all",
        });
        setNotifications(fetchedResult.items);
      } catch (error) {
        console.error("Failed to load notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    // Load on mount
    loadNotifications();
  }, []); // Only run on mount

  // ✅ Refresh notifications when dropdown opens
  useEffect(() => {
    if (!open) return;

    const loadNotifications = async () => {
      const globalUserId = String(
        getAuthCookie("globalUserId") || ""
      ).trim();
      const token = getAuthCookie("sessionToken");
      
      if (!globalUserId || !token) {
        return;
      }

      try {
        const fetchedResult = await fetchNotifications(globalUserId, token, {
          page: 1,
          limit: 10,
          seen: "all",
        });
        setNotifications(fetchedResult.items);
      } catch (error) {
        console.error("Failed to refresh notifications:", error);
      }
    };

    loadNotifications();
  }, [open]);

  // ✅ Watch for unread count increases and refetch notifications
  useEffect(() => {
    // Only refetch if count increased (not decreased or same)
    if (unreadCount > previousUnreadCountRef.current && previousUnreadCountRef.current > 0) {
      // console.log(`[NotificationDropdown] 🔄 Unread count increased from ${previousUnreadCountRef.current} to ${unreadCount}, refetching notifications`);
      
      const loadNotifications = async () => {
        const globalUserId = String(
          getAuthCookie("globalUserId") || ""
        ).trim();
        const token = getAuthCookie("sessionToken");
        
        if (!globalUserId || !token) {
          return;
        }

        try {
          const fetchedResult = await fetchNotifications(globalUserId, token, {
            page: 1,
            limit: 10,
            seen: "all",
          });
          setNotifications(fetchedResult.items);
        } catch (error) {
          console.error("Failed to refetch notifications:", error);
        }
      };

      loadNotifications();
    }
    
    // Update previous count
    previousUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  // ✅ Listen for WebSocket unread_count events
  useEffect(() => {
    // console.log("[NotificationDropdown] 👂 Setting up WebSocket listener for unread_count");

    const handleUnreadCount = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      
      // console.log("[NotificationDropdown] 🔢 WebSocket unread_count event received:", data);
      
      // Check for different possible formats
      let count = 0;
      
      if (typeof data === 'number') {
        count = data;
      } else if (typeof data === 'object' && data !== null) {
        // Try different property names
        count = data.count || data.unread_count || data.unreadCount || data.total || 0;
      }
      
      // console.log(`[NotificationDropdown] 🔢 Setting unread count to: ${count}`);
      setUnreadCount(count);
    };

    // Also listen for notification events that might contain count
    const handleNotification = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      
      // console.log("[NotificationDropdown] 🔔 Notification event received:", data);
      
      // If notification has unread_count property
      if (data?.unread_count !== undefined) {
        // console.log(`[NotificationDropdown] 🔢 Notification contains unread_count: ${data.unread_count}`);
        setUnreadCount(data.unread_count);
      }
    };

    // Listen to all WebSocket message events to find unread_count
    const handleWebSocketMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      
      // console.log("[NotificationDropdown] 📨 Raw WebSocket message:", data);
      
      // Check if this is an unread_count message
      if (data?.type === "unread_count" || data?.event === "unread_count") {
        const count = data.count || data.unread_count || 0;
        // console.log(`[NotificationDropdown] 🔢 Found unread_count in WebSocket: ${count}`);
        setUnreadCount(count);
      }
      
      // Also check if message has unread_count property
      if (data?.unread_count !== undefined) {
        // console.log(`[NotificationDropdown] 🔢 Message has unread_count: ${data.unread_count}`);
        setUnreadCount(data.unread_count);
      }
    };

    // Set up event listeners
    window.addEventListener("realesy:unread_count", handleUnreadCount);
    window.addEventListener("realesy:notification", handleNotification);
    window.addEventListener("realesy:websocket_message", handleWebSocketMessage);
    
    // Also check for initial count from any other events
    const handleAllMessages = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      
      // Try to extract count from any message
      if (data?.unread_count !== undefined) {
        // console.log(`[NotificationDropdown] 🔍 Found unread_count in ${event.type}: ${data.unread_count}`);
        setUnreadCount(data.unread_count);
      }
    };
    
    // Listen to all realesy events to catch any that might contain count
    window.addEventListener("realesy:message", handleAllMessages);
    window.addEventListener("realesy:chat_message", handleAllMessages);

    return () => {
      // console.log("[NotificationDropdown] 🧹 Cleaning up WebSocket listeners");
      window.removeEventListener("realesy:unread_count", handleUnreadCount);
      window.removeEventListener("realesy:notification", handleNotification);
      window.removeEventListener("realesy:websocket_message", handleWebSocketMessage);
      window.removeEventListener("realesy:message", handleAllMessages);
      window.removeEventListener("realesy:chat_message", handleAllMessages);
    };
  }, []); // Empty dependency array - set up once

  // Sort: unread first, then recent
  const sorted = [...notifications].sort((a, b) => {
    if (a.isUnread === b.isUnread) return 0;
    return a.isUnread ? -1 : 1;
  });

  // Limit to 6 for dropdown
  const topSix = sorted.slice(0, 6);

  // ✅ Detect mobile and calculate button position
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      if (mobile && buttonRef.current && open) {
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 400;
        
        let top: number;
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          top = rect.top - dropdownHeight - 10;
          if (top < 10) top = 10;
        } else {
          top = rect.bottom + 10;
        }
        
        setButtonPosition({
          top: Math.max(10, top),
          right: Math.max(16, window.innerWidth - rect.right),
        });
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    if (isMobile && open) {
      window.addEventListener("scroll", checkMobile, true);
    }

    return () => {
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener("scroll", checkMobile, true);
    };
  }, [open, isMobile]);

  // ✅ Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      document.body.classList.add("dropdown-open");
    } else {
      document.body.classList.remove("dropdown-open");
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.body.classList.remove("dropdown-open");
    };
  }, [open]);

  // ✅ Handle escape key to close
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="notification-dropdown ms-3 position-relative">
      {/* BELL ICON */}
      <button
        ref={buttonRef}
        className="btn position-relative p-0 border-0 bg-transparent"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        type="button"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <Bell size={26} color="#4b5563" />
        
        {/* UNREAD COUNT BADGE - FROM WEBSOCKET */}
        {unreadCount > 0 && (
          <span
            className="position-absolute d-flex align-items-center justify-content-center"
            style={{
              top: -4,
              right: -4,
              minWidth: "20px",
              height: "20px",
              backgroundColor: "#155dfc",
              color: "#ffffff",
              borderRadius: "10px",
              fontSize: "11px",
              fontWeight: "600",
              padding: "0 6px",
              border: "2px solid #ffffff",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
              zIndex: 1,
            }}
            aria-live="polite"
            aria-atomic="true"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* BACKDROP FOR MOBILE */}
      {open && isMobile && (
        <div
          className="notification-backdrop"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 1049,
          }}
        />
      )}

      {/* DROPDOWN MENU */}
      {open && (
        <div
          ref={dropdownRef}
          className={`notification-dropdown-menu ${isMobile ? "mobile-dropdown" : ""}`}
          style={
            isMobile
              ? {
                  position: "fixed",
                  top: buttonPosition.top > 0 ? `${buttonPosition.top}px` : "70px",
                  right: buttonPosition.right > 0 ? `${buttonPosition.right}px` : "16px",
                  zIndex: 1050,
                }
              : {
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 10px)",
                  zIndex: 1050,
                }
          }
          role="dialog"
          aria-label="Notifications dropdown"
        >
          {/* HEADER */}
          <div className="px-3 py-3 border-bottom">
            <div>
              <h6 className="m-0 fw-semibold" style={{ fontSize: "15px" }}>
                Notifications
              </h6>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {unreadCount > 0 
                  ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""}`
                  : "No unread notifications"}
              </div>
            </div>
          </div>

          {/* SCROLLABLE LIST */}
          <div className="notification-list">
            {loading && (
              <div className="text-center text-muted py-4">Loading notifications...</div>
            )}
            
            {!loading && topSix.length === 0 && (
              <div className="text-center text-muted py-4">No notifications</div>
            )}

            {!loading && topSix.map((item) => (
              <div className="mb-2" key={item.id}>
                <NotificationItem
                  item={item}
                  isDropdown
                  onClick={handleNotificationClick}
                />
              </div>
            ))}
          </div>

          {/* VIEW ALL BUTTON */}
          <div className="border-top p-2 d-flex justify-content-center">
            <Link
              to={`/${login}/notifications`}
              className="btn w-100 notification-view-all-btn"
              onClick={() => setOpen(false)}
              style={{
                background: "#155dfc",
                color: "#fff",
                fontSize: "14px",
                borderRadius: "8px",
                padding: "8px 0",
                textDecoration: "none",
                border: "none",
              }}
            >
              View All Notifications
            </Link>
          </div>
        </div>
      )}

      {/* STYLES */}
      <style>{`
        .notification-dropdown {
          position: relative;
        }

        .notification-backdrop {
          animation: fadeIn 0.2s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .notification-dropdown-menu {
          width: 360px;
          max-width: calc(100vw - 20px);
          max-height: 480px;
          padding: 0;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
          background: #fff;
          display: flex;
          flex-direction: column;
          animation: slideDown 0.2s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .notification-list {
          max-height: 360px;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 10px 12px;
          flex: 1;
          -webkit-overflow-scrolling: touch;
        }

        .notification-list::-webkit-scrollbar {
          width: 6px;
        }

        .notification-list::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }

        .notification-list::-webkit-scrollbar-track {
          background: transparent;
        }

        .unread-bg {
          background: #eef4ff !important;
        }

        .notification-view-all-btn {
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .notification-view-all-btn:hover {
          background: #0d4fc7 !important;
          color: #fff !important;
        }

        .notification-view-all-btn:active {
          transform: scale(0.98);
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .notification-dropdown-menu.mobile-dropdown {
            width: calc(100vw - 32px) !important;
            max-width: 360px !important;
            max-height: calc(100vh - 100px) !important;
            left: auto !important;
            right: 16px !important;
            position: fixed !important;
            transform: none !important;
            animation: slideDownMobile 0.25s ease-out;
          }

          @keyframes slideDownMobile {
            from {
              opacity: 0;
              transform: translateY(-20px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          .notification-list {
            max-height: calc(100vh - 220px) !important;
            padding: 8px 10px;
          }

          .notification-dropdown {
            margin-left: 0.5rem !important;
          }

          .notification-view-all-btn {
            padding: 12px 0 !important;
            font-size: 15px !important;
            min-height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 500;
          }
        }

        /* Prevent body scroll on mobile when dropdown is open */
        body.dropdown-open {
          overflow: hidden !important;
          position: fixed !important;
          width: 100% !important;
        }

        /* Improve touch targets on mobile */
        @media (max-width: 768px) {
          .notification-dropdown button {
            min-width: 44px;
            min-height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      `}</style>
      <MembershipViewAccessModal
        open={Boolean(accessPrompt)}
        message={accessPrompt?.message || ""}
        requesting={requestingAccess}
        onCancel={() => {
          if (!requestingAccess) setAccessPrompt(null);
        }}
        onRequestAccess={handleRequestAccess}
      />
    </div>
  );
};

export default NotificationDropdown;
